import { useState, useRef, useCallback } from "react";
import { api } from "@/lib/api";
import UploadedFileName from "@/components/feature/UploadedFileName";
import MediaLibraryModal from "@/components/feature/MediaLibraryModal";

interface PdfUploadFieldProps {
  value: string;
  onChange: (url: string) => void;
  label?: string;
}

type UploadResult = { url: string; originalFilename?: string | null };

export default function PdfUploadField({ value, onChange, label = "PDF" }: PdfUploadFieldProps) {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [originalFileName, setOriginalFileName] = useState<string | null>(null);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const uploadFile = useCallback(async (file: File) => {
    const isPdf =
      file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
    if (!isPdf) {
      alert("PDF 파일만 업로드할 수 있습니다.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      alert("파일 크기는 10MB를 초과할 수 없습니다.");
      return;
    }
    setUploading(true);
    try {
      const res = await api.upload<UploadResult>("/admin/files/pdfs", file);
      if (res.data?.url) {
        onChange(res.data.url);
        setOriginalFileName(res.data.originalFilename?.trim() || file.name);
      }
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : "업로드에 실패했습니다.");
    } finally {
      setUploading(false);
    }
  }, [onChange]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) uploadFile(file);
  }, [uploadFile]);

  const clearFile = () => {
    setOriginalFileName(null);
    onChange("");
  };

  return (
    <div>
      <label className="block text-sm font-medium text-[#2c4964] mb-1.5">{label}</label>
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setLibraryOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#1977cc] border border-[#1977cc]/30 rounded-md bg-[#f1f7fc] hover:bg-[#e8f2fb] cursor-pointer"
          >
            <i className="ri-folder-open-line" />
            라이브러리에서 선택
          </button>
        </div>
        <input
          type="text"
          value={value}
          onChange={(e) => {
            setOriginalFileName(null);
            onChange(e.target.value);
          }}
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-[#1977cc] focus:ring-1 focus:ring-[#1977cc]/20"
          placeholder="https://... 또는 업로드"
        />

        <UploadedFileName
          name={originalFileName}
          iconClass="ri-file-pdf-line"
          onRemove={value ? clearFile : undefined}
        />

        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
          className={`relative border-2 border-dashed rounded-lg px-4 py-6 text-center cursor-pointer transition-colors ${
            dragOver ? "border-[#1977cc] bg-[#1977cc]/5" : "border-gray-200 hover:border-gray-300 bg-gray-50"
          }`}
        >
          <input
            ref={inputRef}
            type="file"
            accept="application/pdf,.pdf"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) uploadFile(file);
              e.target.value = "";
            }}
          />
          {uploading ? (
            <div className="flex items-center justify-center gap-2">
              <div className="w-4 h-4 border-2 border-[#1977cc] border-t-transparent rounded-full animate-spin" />
              <span className="text-xs text-[#1977cc]">Uploading...</span>
            </div>
          ) : (
            <>
              <div className="w-8 h-8 mx-auto mb-2 flex items-center justify-center rounded-full bg-white text-gray-400">
                <i className="ri-file-upload-line text-lg" />
              </div>
              <p className="text-xs text-gray-500">
                {value ? "클릭하거나 드래그하여 PDF 변경" : "클릭하거나 드래그하여 PDF 업로드"}
                <br />
                <span className="text-gray-400">Max 10MB</span>
              </p>
            </>
          )}
        </div>
      </div>

      <MediaLibraryModal
        open={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        category="pdf"
        onSelect={(url, meta) => {
          onChange(url);
          setOriginalFileName(meta?.originalFilename?.trim() || null);
        }}
      />
    </div>
  );
}
